import { useState } from 'react';
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import * as theme from '../../theme';
import dayjs from 'dayjs';
import Button from "@mui/material/Button";
import ErrorDisplay from '../../components/ErrorDisplay';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ConsentForm from '../../components/ConsentForm';

const functions = getFunctions();

export default function PreScanForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dob: null,
    practitioner: '',
    sex: '',
    female: {
      isPregnant: null,
      isTrying: null,
      isNursing: null,
      ageOfBaby: ''
    },
    isGroupDetox: null,
    groupDetox: '',
    concerns: '',
  });
  const [consentData, setConsentData] = useState({
    agreed: false,
    referringAgree: false,
    signature: '',
    parentGuardian: ''
  });
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const steps = ['Pre-Scan Form', 'Consent'];

  const clearError = () => {
    setError('');
  };

  const calculateAge = () => {
    if (!formData.dob) return 0;
    return dayjs().diff(formData.dob, 'year');
  };

  const isUnder18 = calculateAge() < 18 && calculateAge() > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('female.')) {
      const femaleField = name.split('.')[1];
      setFormData({
        ...formData,
        female: { ...formData.female, [femaleField]: value }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleBooleanChange = (name, value) => {
    if (name.startsWith('female.')) {
      const femaleField = name.split('.')[1];
      let updatedFemale = { ...formData.female, [femaleField]: value };
      if (femaleField === 'isPregnant' && value === true) {
        updatedFemale.isTrying = false;
      }
      setFormData({ ...formData, female: updatedFemale });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleConsentChange = (e) => {
    const { name, value, checked, type } = e.target;
    setConsentData({
      ...consentData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleDateChange = (value) => {
    setFormData({ ...formData, dob: value });
  };

  const validateFormStep = () => {
    // Required fields for all users
    const requiredFields = [
      'firstName', 
      'lastName', 
      'email', 
      'dob', 
      'practitioner', 
      'sex'
    ];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`Missing required field: ${field}`);
        return false;
      }
    }

    // Validate sex
    if (!['M', 'F'].includes(formData.sex)) {
      setError('Please select a valid sex.');
      return false;
    }

    // Validate Group Detox fields
    if (formData.isGroupDetox === null) {
      setError('Please indicate if you are part of a Cohort or Group Detox program.');
      return false;
    }
    if (formData.isGroupDetox === true && !formData.groupDetox) {
      setError('Please specify the Cohort or Group Detox program.');
      return false;
    }

    // Validate female-specific fields if sex is Female and age >= 15
    if (formData.sex === 'F' && calculateAge() >= 15) {
      if (formData.female.isPregnant === null) {
        setError('Please indicate if you are pregnant.');
        return false;
      }
      if (formData.female.isPregnant === false && formData.female.isTrying === null) {
        setError('Please indicate if you are actively trying to get pregnant.');
        return false;
      }
      if (formData.female.isNursing === null) {
        setError('Please indicate if you are currently nursing.');
        return false;
      }
      if (formData.female.isNursing === true && !formData.female.ageOfBaby) {
        setError('Please specify the age of your baby.');
        return false;
      }
    }

    return true;
  };

  const validateConsentStep = () => {
    if (!consentData.agreed) {
      setError('You must agree to the consent terms.');
      return false;
    }
    if (!consentData.signature) {
      setError('Please provide your signature.');
      return false;
    }
    if (isUnder18 && !consentData.parentGuardian) {
      setError('Please provide the name of the parent or guardian.');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateFormStep()) {
      return;
    }
    setError('');
    setActiveStep((prev) => prev + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateConsentStep()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        dob: formData.dob ? formData.dob.format('YYYY-MM-DD') : null,
        female: {
          isPregnant: formData.female.isPregnant,
          isTrying: formData.female.isTrying,
          isNursing: formData.female.isNursing,
          ageOfBaby: formData.female.ageOfBaby
        },
        isGroupDetox: formData.isGroupDetox,
        groupDetox: formData.groupDetox,
        concerns: formData.concerns,
        consent: {
          agreed: consentData.agreed,
          referringAgree: consentData.referringAgree,
          signature: consentData.signature,
          parentGuardian: consentData.parentGuardian || null
        }
      };

      const submitPreScanForm = httpsCallable(functions, 'submitPreScanForm');
      const result = await submitPreScanForm(submitData);

      navigate(`/public/thank-you`);
    } catch (err) {
      console.error('Form submission error:', err.message, err);
      setError("Failed to submit form: " + err.message);
    }

    setLoading(false);
  };

  const renderFormStep = () => (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); handleNext(); }}
      sx={{ ...theme.FullForm.form, gap: 4 }}
    >
      <TextField
        label="First Name"
        name="firstName"
        value={formData.firstName}
        onChange={handleChange}
        variant="filled"
        required
      />
      <TextField
        label="Last Name"
        name="lastName"
        value={formData.lastName}
        onChange={handleChange}
        variant="filled"
        required
      />
      <TextField
        label="Email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        variant="filled"
        required
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MobileDatePicker 
          label="Date of Birth"
          value={formData.dob}
          onChange={handleDateChange} 
          maxDate={dayjs()}
          openTo="year"
          views={['year', 'month', 'day']}
          yearsOrder="desc"
          slotProps={{
            textField: {
              variant: "filled",
              required: true
            },
          }}
        />
      </LocalizationProvider>
      <TextField
        label="Practitioner's Name"
        name="practitioner"
        value={formData.practitioner}
        onChange={handleChange}
        variant="filled"
        required
      />
      <FormControl variant="filled" fullWidth>
        <InputLabel>What is your sex?</InputLabel>
        <Select
          name="sex"
          value={formData.sex}
          onChange={handleChange}
          required
        >
          <MenuItem value="M">Male</MenuItem>
          <MenuItem value="F">Female</MenuItem>
        </Select>
      </FormControl>
      {formData.sex === 'F' && calculateAge() >= 15 && (
        <Box sx={{ border: 4, borderColor: theme.Colors.GoldDark, p: 2, borderRadius: 1 }}>
          <FormControl variant="filled" fullWidth>
            <InputLabel>Are you pregnant?</InputLabel>
            <Select
              name="female.isPregnant"
              value={formData.female.isPregnant === null ? '' : formData.female.isPregnant}
              onChange={(e) => handleBooleanChange('female.isPregnant', e.target.value === 'true')}
              required
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          {formData.female.isPregnant === false && (
            <FormControl variant="filled" fullWidth sx={{ mt: 2 }}>
              <InputLabel>Are you actively trying to get pregnant?</InputLabel>
              <Select
                name="female.isTrying"
                value={formData.female.isTrying === null ? '' : formData.female.isTrying}
                onChange={(e) => handleBooleanChange('female.isTrying', e.target.value === 'true')}
                required
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl variant="filled" fullWidth sx={{ mt: 2 }}>
            <InputLabel>Are you currently nursing?</InputLabel>
            <Select
              name="female.isNursing"
              value={formData.female.isNursing === null ? '' : formData.female.isNursing}
              onChange={(e) => handleBooleanChange('female.isNursing', e.target.value === 'true')}
              required
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          {formData.female.isNursing === true && (
            <TextField
              label="How old is your baby?"
              name="female.ageOfBaby"
              value={formData.female.ageOfBaby}
              onChange={handleChange}
              variant="filled"
              fullWidth
              sx={{ mt: 2 }}
              required
            />
          )}
        </Box>
      )}
      <Box sx={{ border: 4, borderColor: theme.Colors.GoldDark, p: 2, borderRadius: 1 }}>
        <FormControl variant="filled" fullWidth>
          <InputLabel>Are you part of a Cohort or Group Detox program?</InputLabel>
          <Select
            name="isGroupDetox"
            value={formData.isGroupDetox === null ? '' : formData.isGroupDetox}
            onChange={(e) => handleBooleanChange('isGroupDetox', e.target.value === 'true')}
            required
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        {formData.isGroupDetox === true && (
          <TextField
            label="Which Cohort or Group Detox program?"
            name="groupDetox"
            value={formData.groupDetox}
            onChange={handleChange}
            variant="filled"
            fullWidth
            sx={{ mt: 2 }}
            required
          />
        )}
      </Box>
      <TextField 
        label="Any Concerns/Goals you would like TruBalance to know?"
        name="concerns"
        value={formData.concerns}
        onChange={handleChange}
        variant="filled"
        multiline
        rows={4} 
        fullWidth
      />
    </Box>
  );

  const renderConsentStep = () => (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ ...theme.FullForm.form, gap: 4, marginBottom: 6 }}
    >
      <Box sx={{ p: 2, border: 1, borderColor: theme.Colors.GrayLight, borderRadius: 1 }}>
        <div style={theme.SubHeadingText}>
          <ConsentForm />
        </div>
      </Box>
      {isUnder18 && (
        <TextField
          label="Parent/Guardian Name"
          name="parentGuardian"
          value={consentData.parentGuardian}
          onChange={handleConsentChange}
          variant="filled"
          required
          fullWidth
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            name="referringAgree"
            checked={consentData.referringAgree}
            onChange={handleConsentChange}
            required
          />
        }
        label="I authorize TruBalance Bioenergetics to share my scan results with my referring practitioner for review."
      />
      <FormControlLabel
        control={
          <Checkbox
            name="agreed"
            checked={consentData.agreed}
            onChange={handleConsentChange}
            required
          />
        }
        label="Yes, I agree to the terms of the bioenergetic/meridian stress assessment testing."
      />
      <TextField
        label="Signature (Type Your Full Name)"
        name="signature"
        value={consentData.signature}
        onChange={handleConsentChange}
        variant="filled"
        required
        fullWidth
      />
    </Box>
  );

  return (
    <div style={{ ...theme.Container.basicWide, marginTop: 20 }}>
      <div style={theme.Heading}>Client Pre-Scan Questionnaire</div>
      <div style={theme.SubHeadingText}>
        Fill out the form below prior to mailing back your samples. If filling out for a child, fill this out as if you are the child being scanned.
      </div>
      <div style={{ marginTop: 15, marginBottom: 30 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {error && (
          <Box sx={{ p: 2, borderRadius: 1, mb: 2 }}>
            <ErrorDisplay
              message={error}
              type="error"
              clearMessage={clearError}
            />
          </Box>
        )}
        {activeStep === 0 ? renderFormStep() : renderConsentStep()}
        <Box sx={{ display: 'flex', width: '100%', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ flex: 1 }}
            onClick={activeStep === 0 ? () => navigate('/public/client-forms') : handleBack}
            disabled={loading || activeStep === 0}
          >
            {'Back'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ flex: 3 }}
            type={activeStep === 1 ? 'submit' : 'button'}
            onClick={activeStep === 0 ? handleNext : handleSubmit}
            disabled={
              loading ||
              (activeStep === 0 && (
                !formData.firstName ||
                !formData.lastName ||
                !formData.email ||
                !formData.dob ||
                !formData.practitioner ||
                !['M', 'F'].includes(formData.sex) ||
                formData.isGroupDetox === null ||
                (formData.isGroupDetox === true && !formData.groupDetox) ||
                (formData.sex === 'F' && calculateAge() >= 15 && (
                  formData.female.isPregnant === null ||
                  (formData.female.isPregnant === false && formData.female.isTrying === null) ||
                  formData.female.isNursing === null ||
                  (formData.female.isNursing === true && !formData.female.ageOfBaby)
                ))
              )) ||
              (activeStep === 1 && (
                !consentData.agreed ||
                !consentData.signature ||
                !consentData.referringAgree ||
                (isUnder18 && !consentData.parentGuardian)
              ))
            }
          >
            {loading ? 'Submitting...' : activeStep === 0 ? 'Next' : 'Submit'}
          </Button>
        </Box>
      </div>
    </div>
  );
}